import { useState, useEffect } from 'react';
import { 
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Flag,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';

const API = `${window.location.origin}/api`;

const AdminClaims = () => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [pagination, setPagination] = useState({ page: 1, pages: 1 });
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const fetchClaims = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString() });
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await fetch(`${API}/admin/claims?${params.toString()}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setClaims(data.claims);
        setPagination({ page: data.page, pages: data.pages });
      }
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClaims();
  }, [statusFilter]);

  const updateClaimStatus = async (claimId, status, reason = null) => {
    try {
      const body = { status };
      if (reason) body.rejection_reason = reason;

      const response = await fetch(`${API}/admin/claims/${claimId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(`Claim ${status}`);
        fetchClaims(pagination.page);
      } else {
        toast.error('Failed to update claim');
      }
    } catch (error) {
      console.error('Error updating claim:', error);
      toast.error('Failed to update claim');
    }
  };

  const handleReject = () => {
    if (selectedClaim) {
      updateClaimStatus(selectedClaim, 'rejected', rejectionReason);
      setRejectDialogOpen(false);
      setSelectedClaim(null);
      setRejectionReason('');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="verified-badge"><CheckCircle className="w-3 h-3" />Approved</span>;
      case 'pending':
        return <span className="pending-badge"><Clock className="w-3 h-3" />Pending</span>;
      case 'rejected':
        return <span className="rejected-badge"><XCircle className="w-3 h-3" />Rejected</span>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Manage</p>
          <h1 className="text-3xl font-bold text-white tracking-tight">Business Claims</h1>
        </div>

        <div className="flex gap-2">
          {['pending', 'approved', 'rejected', ''].map((status) => (
            <button
              key={status || 'all'}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === status
                  ? 'bg-white text-black'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white'
              }`}
              data-testid={`claims-filter-${status || 'all'}`}
            >
              {status || 'All'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 skeleton rounded-lg" />
          ))}
        </div>
      ) : claims.length > 0 ? (
        <>
          <div className="space-y-4">
            {claims.map((claim) => (
              <motion.div
                key={claim.claim_id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="glass-card rounded-xl p-6"
                data-testid={`admin-claim-${claim.claim_id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{claim.business_name}</h3>
                      {getStatusBadge(claim.status)}
                    </div>
                    <p className="text-zinc-500 text-sm mb-2">{claim.business_category}</p>
                    
                    <div className="bg-zinc-900/50 rounded-lg p-4 mt-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-zinc-500">Claimed by:</span>
                        <span className="text-white">{claim.user_name}</span>
                        <span className="text-zinc-600">({claim.user_email})</span>
                      </div>
                      {claim.contact_phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-zinc-500">Phone:</span>
                          <span className="text-white">{claim.contact_phone}</span>
                        </div>
                      )}
                      {claim.proof_description && (
                        <div className="text-sm">
                          <span className="text-zinc-500">Proof:</span>
                          <p className="text-zinc-300 mt-1">{claim.proof_description}</p>
                        </div>
                      )}
                    </div>

                    {claim.rejection_reason && (
                      <p className="text-red-400 text-sm mt-3">Rejection: {claim.rejection_reason}</p>
                    )}
                  </div>

                  {claim.status === 'pending' && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateClaimStatus(claim.claim_id, 'approved')}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors"
                        data-testid={`approve-claim-${claim.claim_id}`}
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve
                      </button>
                      <button
                        onClick={() => {
                          setSelectedClaim(claim.claim_id);
                          setRejectDialogOpen(true);
                        }}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                        data-testid={`reject-claim-${claim.claim_id}`}
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => fetchClaims(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-zinc-800 text-zinc-400 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-4 font-mono text-sm text-zinc-400">
                {pagination.page} / {pagination.pages}
              </span>
              <button
                onClick={() => fetchClaims(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-zinc-800 text-zinc-400 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20">
          <Flag className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-500">No claims found.</p>
        </div>
      )}

      {/* Reject Dialog */}
      <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <AlertDialogContent className="bg-[#0F0F0F] border border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Reject Claim</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Provide a reason for rejection (optional but recommended).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter rejection reason..."
            className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
            data-testid="claim-rejection-reason-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-reject-claim-button"
            >
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export { AdminClaims };
export default AdminClaims;
